/**
 * A linked credit-note line and the original line it refunds.
 */
import { roundMoney } from "./money.js";
import { exactScaled } from "./tax-calculator.js";
/**
 * The share of an original line's discount a credit of `quantity` of its
 * `originalQuantity` units hands back. A line discount is an amount for the
 * whole line (`quantity × unitPrice − discount`), so the share is
 * `discount × quantity / originalQuantity`, to the centime (nearest, an
 * exact half away from zero).
 */
export function proratedLineDiscount(discount, quantity, originalQuantity) {
    if (![discount, quantity, originalQuantity].every(Number.isFinite) || discount < 0 || quantity < 0) {
        throw new Error(`Remise ou quantité invalide : remise ${discount}, quantité ${quantity} sur ${originalQuantity}`);
    }
    if (originalQuantity <= 0) {
        throw new Error("La quantité de la ligne d'origine doit être supérieure à zéro");
    }
    return roundMoney((discount * quantity) / originalQuantity);
}
/** Whole quantities tried on each side of an entered credited quantity. */
const CREDITED_QUANTITY_CANDIDATES = 50;
/**
 * The quantities proposed instead of `entered` units of a line of which
 * `remaining` units are left to credit, nearest to `entered` first, the
 * lower one first on a tie: the whole quantities around `entered` up to
 * `remaining`, and `remaining` itself.
 */
export function creditedQuantityCandidates(entered, remaining) {
    const candidates = new Set();
    const low = Math.max(1, Math.floor(entered) - CREDITED_QUANTITY_CANDIDATES);
    const high = Math.min(Math.floor(remaining), Math.ceil(entered) + CREDITED_QUANTITY_CANDIDATES);
    for (let quantity = low; quantity <= high; quantity++)
        candidates.add(quantity);
    if (remaining > 0)
        candidates.add(remaining);
    candidates.delete(entered);
    return [...candidates].sort((a, b) => Math.abs(a - entered) - Math.abs(b - entered) || a - b);
}
/**
 * The discount a linked credit-note line returning `quantity` units takes
 * from its original line: a return of exactly what remains takes what remains
 * of the discount, so the credits of one line sum to it; any other quantity,
 * or a remainder whose discount is unknown or below zero, takes its share
 * (`proratedLineDiscount`) and the price reduction of each unit it returns.
 */
export function creditLineDiscount(quantity, remainder) {
    const { remainingDiscount } = remainder;
    if (quantity === remainder.remainingQuantity && remainingDiscount !== null && remainingDiscount >= 0) {
        return remainingDiscount;
    }
    const share = proratedLineDiscount(remainder.originalDiscount, quantity, remainder.originalQuantity);
    return roundMoney(share + roundMoney(quantity * (remainder.priceReduction ?? 0)));
}
/**
 * Whether a credit-note line linked to an original line is priced otherwise
 * than that line. On a COR credit note covering every unit left it is a price
 * credit: it credits a unit-price difference and returns no unit.
 */
export function isPriceCreditLine(unitPrice, originalUnitPrice) {
    return Math.round(Number(unitPrice) * 100) !== Math.round(Number(originalUnitPrice) * 100);
}
/**
 * Whether a COR price credit of `difference` on each of `quantity` units
 * credits an amount to the centime. The line credits that amount rounded,
 * while the returns after it take `quantity × difference` as discount: when
 * the two differ, the rest of the line can no longer be credited to its end.
 * False when the quantity is finer than the thousandth or the difference
 * finer than the centime.
 */
export function priceCreditToTheCentime(quantity, difference) {
    const quantityT = exactScaled(quantity, 1000);
    const differenceH = exactScaled(difference, 100);
    if (quantityT === null || differenceH === null)
        return false;
    return ((quantityT % 1000) * (differenceH % 1000)) % 1000 === 0;
}
/**
 * New unit prices tried on each side of an entered one, in centimes: any
 * 1 000 consecutive drops hold one `priceCreditToTheCentime` accepts.
 */
const PRICE_CREDIT_CANDIDATES = 1000;
/**
 * The new unit price nearest `newPrice`, other than it, above 0 and below
 * `from`, the price the units are sold at now, whose drop from `from` on
 * `quantity` units is to the centime (`priceCreditToTheCentime`) and that
 * `accepts` accepts, the lower one first on a tie; null when none is.
 */
export function priceCreditProposal(quantity, from, newPrice, accepts) {
    const fromH = Math.round(from * 100);
    const enteredH = Math.round(newPrice * 100);
    for (let step = 1; step <= PRICE_CREDIT_CANDIDATES; step++) {
        for (const candidateH of [enteredH - step, enteredH + step]) {
            if (candidateH < 1 || candidateH >= fromH)
                continue;
            const difference = (fromH - candidateH) / 100;
            if (!priceCreditToTheCentime(quantity, difference))
                continue;
            const accepted = accepts(difference);
            if (accepted !== null)
                return { newPrice: candidateH / 100, difference, accepted };
        }
    }
    return null;
}
/**
 * The nearest quantity left to credit of a line, other than `entered`
 * (`creditedQuantityCandidates`), that `accepts` accepts with the discount
 * that quantity takes (`creditLineDiscount`), with what `accepts` returned;
 * null when it accepts none.
 */
export function creditedQuantityProposal(entered, remainder, accepts) {
    for (const quantity of creditedQuantityCandidates(entered, remainder.remainingQuantity)) {
        const discount = creditLineDiscount(quantity, remainder);
        const accepted = accepts(quantity, discount);
        if (accepted !== null)
            return { quantity, discount, accepted };
    }
    return null;
}
//# sourceMappingURL=credit-note-line.js.map